import { Map } from 'components/map';
import { atom, useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import type GeoJSON from 'geojson';
import { Layer, MapRef, Source } from 'react-map-gl';
import { createMapSourceAtom } from 'components/map/atoms';
import { mapColors } from 'components/map/map-colors';
import { Line } from 'components/map/well';
import { LINE_STYLES } from 'components/map/map-styles';
import { ESTIMATED_ALPHA } from 'components/well-page-content/gun-barrel-marker-style';
import { subsurfaceScopedLayersVisibilityAtomFamily } from 'components/well-page-content/subsurface-explorer/scoped-layers-atoms';
import { SubsurfaceExplorerScopedLayers } from 'components/well-page-content/subsurface-explorer/subsurface-explorer-scoped-layers';
import {
  NEARBY_LINE_WIDTH,
  OFFSET_LINE_WIDTH,
} from 'components/well-page-content/subsurface-explorer/scoped-layers-constants';
import type {
  IntersectingUnit,
  WellboreMapData,
  WellIntersectionAnalysisResponse,
} from 'types/api-responses';
import { wellSelectorMapConfigAtom } from './utils';
import { buildArrowPolygon } from './arrow-polygon';
import { ScopedWellsTileLayers } from './scoped-wells-tile-layers';
import styles from './well-selector-map.module.scss';
import { styleInsetMainWells } from './style-inset-main-wells';
import { darkenColor } from 'components/well-page-content/utils';
import { WELL_STATUS_COLOR_MAP } from 'utils/well-status';
import { WELL_STATUS_SLOT_CATEGORY_KEYS } from 'utils/slot-categories';

/**
 * Main-map scoped-layer data reused on the inset (INS-8658 follow-up): the
 * subject well's wellbores + the intersecting-units polygons, both already
 * fetched at the well-page level. No extra API call.
 */
export interface InsetScopedLayers {
  intersectionData?: WellIntersectionAnalysisResponse;
  selectedUnitIds?: Set<string>;
  subjectWellbores?: WellboreMapData[];
}

const mapRefAtom = atom<MapRef | null>(null);

// Per-status highlight color (~12% darker than wells-v2 base) so the highlight
// reads as emphasis without losing the well's color identity.
const STATUS_HIGHLIGHT_COLOR: Record<string, string> = {
  producing: '#3776A8',
  duc: '#E08323',
  permit: '#E0589D',
  shut_in: '#3F3F3F',
  expired_permit: '#7A7A7A',
  inactive: '#B0B0B0',
  // The Create New Well statuses: the shared colours, 12% darker (INS-13300).
  ...Object.fromEntries(
    WELL_STATUS_SLOT_CATEGORY_KEYS.map((status) => [
      status,
      darkenColor(WELL_STATUS_COLOR_MAP[status], 0.88),
    ])
  ),
};
const DEFAULT_HIGHLIGHT_COLOR = '#4D4D4D';

// Mapbox `match` expression form for wells-v2 tile-filter layers.
const STATUS_HIGHLIGHT_MATCH_EXPRESSION: unknown[] = [
  'match',
  ['get', 'status'],
  ...Object.entries(STATUS_HIGHLIGHT_COLOR).flat(),
  DEFAULT_HIGHLIGHT_COLOR,
];

// Shared paint configs — halo is a wider translucent glow behind the bolder line.
const HIGHLIGHT_HALO_PAINT = {
  'line-width': 8,
  'line-opacity': 0.35,
  'line-blur': 2,
} as const;
const HIGHLIGHT_LINE_PAINT = {
  'line-width': 4,
  'line-opacity': 1,
} as const;

// Estimated (synthesized) vertical bore marker on the inset. A vertical's survey
// is a near-zero-length line — invisible as a plan-view line — so it is drawn as
// a surface-hole circle in the faded estimated treatment (ESTIMATED_ALPHA),
// matching the 3D viewer + 2D Profile estimated styling.
const ESTIMATED_MARKER_RADIUS = 4;

// Reads the plan-view surface coordinate for an estimated bore: the explicit
// surfaceHole point when present, else the first vertex of the survey geometry
// (SHL). Handles both a bare GeoJSON geometry and a Feature, since the wellbores
// payload carries a bare LineString geometry under `survey`.
const surfacePointForEstimatedBore = (
  wb: WellboreMapData
): GeoJSON.Position | null => {
  if (wb.surfaceHole?.coordinates) return wb.surfaceHole.coordinates;

  const survey = wb.survey as
    GeoJSON.Feature | GeoJSON.Geometry | null | undefined;
  const geometry =
    survey && 'geometry' in survey ? survey.geometry : (survey ?? null);
  if (geometry?.type === 'LineString' && geometry.coordinates.length > 0) {
    return geometry.coordinates[0];
  }
  return null;
};

const WellSelectorMap = ({
  coordinates,
  radiusInFeet,
  searchEnvelopePolygon,
  sceneExtentPolygon,
  activeSliceCoords,
  wellAzimuthDegree,
  sliceHalfWidthFt,
  sliceFlipped = false,
  highlightedSurvey = null,
  highlightedSurveyStatus = null,
  highlightedUwi = null,
  showCircle = true,
  showSlice = true,
  landingAnalysisMode = false,
  subjectUwi,
  nearbyTilesUrl = null,
  offsetTilesUrl = null,
  insetScopedLayers,
  cooperativeGestures = false,
}: {
  coordinates?: { latitude: number; longitude: number };
  radiusInFeet?: number;
  searchEnvelopePolygon?: GeoJSON.Polygon;
  sceneExtentPolygon?: GeoJSON.Polygon;
  activeSliceCoords?: { latitude: number; longitude: number };
  wellAzimuthDegree?: number;
  sliceHalfWidthFt?: number;
  sliceFlipped?: boolean;
  /** GeoJSON survey to highlight (subject well — fetched via wellMapResult). */
  highlightedSurvey?: GeoJSON.Feature | null;
  /** Status of `highlightedSurvey`; picks the color from STATUS_HIGHLIGHT_COLOR. */
  highlightedSurveyStatus?: string | null;
  /** UWI to highlight via the wells-v2 tile filter (offset wells — no extra fetch). */
  highlightedUwi?: string | null;
  showCircle?: boolean;
  /** When false, hides the gun barrel slice rectangle on the map (triangle arrows still show) */
  showSlice?: boolean;
  /** When true, direction arrows point east/west based on azimuth instead of along the well */
  landingAnalysisMode?: boolean;
  /** Subject UWI — keys the per-subject scoped-layer visibility state. */
  subjectUwi?: string;
  /** Server-stamped Nearby Wells MVT template (`{geo_attribute}/{z}/{x}/{y}`). */
  nearbyTilesUrl?: string | null;
  /** Server-stamped Offset Wells MVT template; absent when no completed spacing job. */
  offsetTilesUrl?: string | null;
  /** Subject + Intersecting-Units data reused from the well page (INS-8658 follow-up). */
  insetScopedLayers?: InsetScopedLayers;
  /**
   * Mapbox's cooperative gestures: a bare wheel scrolls the page and Ctrl/⌘ + wheel
   * zooms the map. On when this map sits inside something that scrolls (a rail) —
   * otherwise the map's default wheel-zoom swallows every wheel over it and that
   * scroll never happens. Off, the map zooms on wheel as it always has.
   */
  cooperativeGestures?: boolean;
}) => {
  const DEFAULT_COORDINATES = {
    latitude: 31.72,
    longitude: -102.26,
  };

  const mapConfig = useAtomValue(wellSelectorMapConfigAtom);

  // The Story-2 offset highlight filters source="wells-v2". When Main Wells is
  // toggled off, WellsV2Layer (which normally provides that source) is not
  // rendered — so mount a bare wells-v2 source here to keep the highlight working.
  const wellsV2SourceAtom = useMemo(() => createMapSourceAtom('wellsV2'), []);
  const wellsV2Source = useAtomValue(wellsV2SourceAtom);

  // Per-subject visibility for the Nearby / Offset scoped layers (INS-8658).
  const scopedVisibility = useAtomValue(
    subsurfaceScopedLayersVisibilityAtomFamily(subjectUwi ?? '')
  );

  // Main-map scoped layers reused on the inset (subject well + intersecting
  // units) — derived from data threaded down from the well page; no extra fetch.
  const { intersectionData, selectedUnitIds, subjectWellbores } =
    insetScopedLayers ?? {};
  const hasUnits = (intersectionData?.intersectingUnits.length ?? 0) > 0;
  const selectedUnits = useMemo(
    () =>
      intersectionData?.intersectingUnits.filter((u) =>
        selectedUnitIds?.has(u.name)
      ) ?? [],
    [intersectionData, selectedUnitIds]
  );
  // The "Subject" layer mirrors the main map's producing-wellbore rendering.
  const subjectProducingWellbores = useMemo(
    () => (subjectWellbores ?? []).filter((wb) => wb.type === 'producing'),
    [subjectWellbores]
  );

  // Estimated (synthesized vertical) subject bores collapse to a point in plan
  // view, so they get a surface-hole circle marker instead of a line.
  const estimatedMarkerData = useMemo<GeoJSON.FeatureCollection | null>(() => {
    const features = (subjectWellbores ?? [])
      .filter((wb) => wb.estimated)
      .flatMap((wb) => {
        const coordinates = surfacePointForEstimatedBore(wb);
        if (!coordinates) return [];
        return [
          {
            type: 'Feature' as const,
            geometry: { type: 'Point' as const, coordinates },
            properties: { uwi14: wb.uwi14 },
          },
        ];
      });
    return features.length ? { type: 'FeatureCollection', features } : null;
  }, [subjectWellbores]);

  // Restyle the inset's Main Wells (wells-v2) layers for contrast: thin the
  // wellbore lines and hide the surface/bottom-hole points (per Product). Done
  // imperatively on THIS map instance only (via its own ref) — the shared
  // WellsV2Layer and the main Well Page map are untouched. Re-applied whenever
  // the map settles (covers the wells-v2 layers being added when the user
  // toggles Main Wells on, and any style reload).
  const mapRef = useAtomValue(mapRefAtom);
  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return undefined;

    const styleInsetWells = () => styleInsetMainWells(map);

    styleInsetWells();
    map.on('idle', styleInsetWells);
    return () => {
      map.off('idle', styleInsetWells);
    };
  }, [mapRef]);

  // Map stays centered on the subject well surface location.
  // The gun barrel slice tracks activeSliceCoords visually without panning the camera.
  const latitude = coordinates?.latitude ?? DEFAULT_COORDINATES.latitude;
  const longitude = coordinates?.longitude ?? DEFAULT_COORDINATES.longitude;

  // Initial zoom scaled from search radius — every doubling of radius reduces
  // zoom by 1 stop, clamped to 8–12 so the search area is always in context.
  // After the map loads, user pan/zoom is preserved (see map.tsx easeTo logic).
  const zoom = useMemo(() => {
    if (!radiusInFeet) return 11;
    return Math.max(
      8,
      Math.min(12, Math.round(12 - Math.log2(Math.max(1, radiusInFeet / 5000))))
    );
  }, [radiusInFeet]);

  // Resolved highlight color for the subject well's wellbore halo + line.
  // Memoized so the JSX below stays a plain expression (no IIFE) and the
  // color string is recomputed only when the status actually changes.
  const highlightedSurveyColor = useMemo(
    () =>
      STATUS_HIGHLIGHT_COLOR[highlightedSurveyStatus ?? ''] ??
      DEFAULT_HIGHLIGHT_COLOR,
    [highlightedSurveyStatus]
  );

  // Pink polygon for the ES search envelope (wellSearchEnvelopeGeometry from wells API).
  // Rectangle tight to the source well's lateral + radius padding — the actual geometry
  // the backend queried against, not a circle approximation of the radius input.
  const searchEnvelopeData = useMemo(() => {
    if (!searchEnvelopePolygon) return null;

    return {
      type: 'FeatureCollection' as const,
      features: [
        {
          type: 'Feature' as const,
          geometry: searchEnvelopePolygon,
          properties: {},
        },
      ],
    };
  }, [searchEnvelopePolygon]);

  // Black polygon for the 3D scene display extent (paddedBoundingBoxGeometry from wells API).
  const boundingBoxData = useMemo(() => {
    if (!sceneExtentPolygon) return null;

    return {
      type: 'FeatureCollection' as const,
      features: [
        {
          type: 'Feature' as const,
          geometry: sceneExtentPolygon,
          properties: {},
        },
      ],
    };
  }, [sceneExtentPolygon]);

  // Gun barrel slice: a rectangle perpendicular to the well azimuth (the search
  // window) with a downward triangular pointer extending from its centre toward
  // the heel, so the combined shape pins the subject-wellbore position at the
  // tip of the triangle.
  const sliceData = useMemo(() => {
    if (!activeSliceCoords || wellAzimuthDegree === undefined || !radiusInFeet)
      return null;

    const azRad = (wellAzimuthDegree * Math.PI) / 180;

    // Perpendicular direction components (azimuth + 90°)
    const perpDx = Math.cos(azRad); // eastward
    const perpDy = -Math.sin(azRad); // northward

    // Along-azimuth components (heel→toe direction)
    const alDx = Math.sin(azRad);
    const alDy = Math.cos(azRad);

    const latDegreesPerFt = 1 / (69 * 5280);
    const lngDegreesPerFt =
      1 / (69 * Math.cos((activeSliceCoords.latitude * Math.PI) / 180) * 5280);

    // Rectangle extents
    const dlat = perpDy * radiusInFeet * latDegreesPerFt;
    const dlon = perpDx * radiusInFeet * lngDegreesPerFt;

    const halfWidthFt = sliceHalfWidthFt ?? Math.max(200, radiusInFeet * 0.02);
    const wlat = alDy * halfWidthFt * latDegreesPerFt;
    const wlon = alDx * halfWidthFt * lngDegreesPerFt;

    // Triangular pointer direction follows the well's azimuth (same convention as
    // the direction arrow).  Not flipped → notch points toward toe (+azimuth
    // direction); flipped → notch points toward heel (anti-azimuth direction).
    const sDir = sliceFlipped ? -1 : 1;

    const triBaseHalfFt = Math.min(radiusInFeet * 0.15, halfWidthFt * 1.5);
    const tLon = perpDx * triBaseHalfFt * lngDegreesPerFt;
    const tLat = perpDy * triBaseHalfFt * latDegreesPerFt;
    // Apex extends one more halfWidth past the south edge in the south direction
    const tHLon = alDx * halfWidthFt * lngDegreesPerFt;
    const tHLat = alDy * halfWidthFt * latDegreesPerFt;

    // South-edge direction offset from centre
    const sWlon = sDir * wlon;
    const sWlat = sDir * wlat;

    const { latitude: lat, longitude: lon } = activeSliceCoords;

    return {
      type: 'FeatureCollection' as const,
      features: [
        {
          type: 'Feature' as const,
          geometry: {
            type: 'Polygon' as const,
            coordinates: [
              [
                // North edge (clockwise)
                [lon + dlon - sWlon, lat + dlat - sWlat], // north-right
                [lon - dlon - sWlon, lat - dlat - sWlat], // north-left
                // South-left corner
                [lon - dlon + sWlon, lat - dlat + sWlat],
                // Triangle notch on south edge
                [lon - tLon + sWlon, lat - tLat + sWlat], // tri base left
                [lon + sDir * (wlon + tHLon), lat + sDir * (wlat + tHLat)], // tri apex
                [lon + tLon + sWlon, lat + tLat + sWlat], // tri base right
                // South-right corner
                [lon + dlon + sWlon, lat + dlat + sWlat],
                // Close
                [lon + dlon - sWlon, lat + dlat - sWlat],
              ],
            ],
          },
          properties: {},
        },
      ],
    };
  }, [
    activeSliceCoords,
    wellAzimuthDegree,
    radiusInFeet,
    sliceHalfWidthFt,
    sliceFlipped,
  ]);

  // Direction arrow at the subject well surface location.
  // Gun Barrel mode: Heel-to-Toe (not flipped) points toward toe, flipped toward heel.
  // Landing Analysis mode: apex pinned on the wellbore, base extends perpendicular
  // to the well on the right-hand side of the heel→toe direction (+90° clockwise
  // from azimuth). Examples: azimuth 345° (northward well, toe-up) → base to the
  // east; azimuth 220° (southwest well, toe-down-left) → base to the northwest.
  const directionArrowData = useMemo(() => {
    if (!coordinates || wellAzimuthDegree === undefined) return null;

    let dirDx: number, dirDy: number, perpDx: number, perpDy: number;

    const azRad = (wellAzimuthDegree * Math.PI) / 180;
    // In Landing Analysis mode the apex is pinned to the well, so baseInward=true
    // means the base is offset in the +(dirDx, dirDy) direction from the well.
    let baseInward = false;

    if (landingAnalysisMode) {
      // (dirDx, dirDy) is the unit vector perpendicular to the wellbore, rotated
      // +90° clockwise from heel→toe — i.e. the right-hand side when walking
      // from heel to toe. The apex stays pinned to the well and the base
      // extends one arrow-length in this direction.
      dirDx = Math.cos(azRad);
      dirDy = -Math.sin(azRad);
      perpDx = Math.sin(azRad);
      perpDy = Math.cos(azRad);
      baseInward = true;
    } else {
      const dirSign = sliceFlipped ? -1 : 1;
      dirDx = dirSign * Math.sin(azRad);
      dirDy = dirSign * Math.cos(azRad);
      perpDx = Math.cos(azRad);
      perpDy = -Math.sin(azRad);
    }

    const arrowLen = Math.min(
      Math.max(radiusInFeet ? radiusInFeet * 0.2 : 400, 400),
      2000
    );
    const halfWidth = arrowLen * 0.35;

    return buildArrowPolygon(
      coordinates.latitude,
      coordinates.longitude,
      dirDx,
      dirDy,
      perpDx,
      perpDy,
      arrowLen,
      halfWidth,
      baseInward
    );
  }, [
    coordinates,
    wellAzimuthDegree,
    radiusInFeet,
    sliceFlipped,
    landingAnalysisMode,
  ]);

  // Direction arrow at the eval point location.
  // Gun Barrel mode: points along/against azimuth based on flip.
  // Landing Analysis mode: apex pinned on the wellbore at the eval point, base
  // extends perpendicular to the well on the right-hand side of the heel→toe
  // direction (+90° clockwise from azimuth). Examples: azimuth 345° (northward
  // well) → base east; azimuth 220° (southwest well) → base northwest.
  const gunBarrelDirectionData = useMemo(() => {
    if (!activeSliceCoords || wellAzimuthDegree === undefined || !radiusInFeet)
      return null;

    let dirDx: number, dirDy: number, perpDx: number, perpDy: number;
    const azRad = (wellAzimuthDegree * Math.PI) / 180;
    let baseInward = false;

    if (landingAnalysisMode) {
      dirDx = Math.cos(azRad);
      dirDy = -Math.sin(azRad);
      perpDx = Math.sin(azRad);
      perpDy = Math.cos(azRad);
      baseInward = true;
    } else {
      const dirSign = sliceFlipped ? -1 : 1;
      dirDx = dirSign * Math.sin(azRad);
      dirDy = dirSign * Math.cos(azRad);
      perpDx = Math.cos(azRad);
      perpDy = -Math.sin(azRad);
    }

    const arrowLen = Math.min(Math.max(radiusInFeet * 0.3, 600), 3000);
    const halfWidth = arrowLen * 0.3;

    return buildArrowPolygon(
      activeSliceCoords.latitude,
      activeSliceCoords.longitude,
      dirDx,
      dirDy,
      perpDx,
      perpDy,
      arrowLen,
      halfWidth,
      baseInward
    );
  }, [
    activeSliceCoords,
    wellAzimuthDegree,
    radiusInFeet,
    sliceFlipped,
    landingAnalysisMode,
  ]);

  return (
    <Map
      refAtom={mapRefAtom}
      longitude={longitude}
      latitude={latitude}
      className={styles.map}
      zoom={zoom}
      mapConfigAtom={wellSelectorMapConfigAtom}
      showControls={false}
      cooperativeGestures={cooperativeGestures}
      showMapFilters={true}
      scopedLayers={
        subjectUwi ? (
          <SubsurfaceExplorerScopedLayers
            uwi={subjectUwi}
            hasOffset={Boolean(offsetTilesUrl)}
            hasUnits={hasUnits}
          />
        ) : undefined
      }
    >
      {/* Keep the wells-v2 source mounted when Main Wells is off so the Story-2
          offset highlight (filters source="wells-v2") still resolves. When Main
          Wells is on, WellsV2Layer in the shared Map provides this source. */}
      {!mapConfig.wellsV2?.checked && wellsV2Source && (
        <Source id="wells-v2" type="vector" url={wellsV2Source} />
      )}

      {/* Development Areas → Intersecting Units — reused from the well page,
          rendered identically to the main map (polygons under the well lines). */}
      {scopedVisibility.units &&
        selectedUnits.map((unit: IntersectingUnit) => {
          if (!unit.geojson) return null;
          return (
            <Source
              key={`inset-unit-source-${unit.id}`}
              type="geojson"
              data={unit.geojson as unknown as GeoJSON.Feature}
            >
              <Layer
                id={`inset-development-area-fill-${unit.name}`}
                type="fill"
                paint={{
                  'fill-color': unit.assignedToUnit
                    ? mapColors.assignedUnitFill
                    : mapColors.nonAssignedUnitFill,
                  'fill-opacity': unit.assignedToUnit ? 0.6 : 0.4,
                }}
              />
              <Layer
                id={`inset-development-area-outline-${unit.name}`}
                type="line"
                paint={{
                  'line-color': unit.assignedToUnit
                    ? mapColors.assignedUnitBorder
                    : mapColors.nonAssignedUnitBorder,
                  'line-width': 2,
                  'line-opacity': 1,
                }}
              />
            </Source>
          );
        })}

      {/* Subsurface Explorer Wells — Nearby / Offset scoped layers (INS-8658).
          Rendered first so the envelope / slice / arrows / highlights draw on top. */}
      {nearbyTilesUrl && scopedVisibility.nearby && (
        <ScopedWellsTileLayers
          idPrefix="nearby-wells"
          urlTemplate={nearbyTilesUrl}
          lineWidth={NEARBY_LINE_WIDTH}
          searchRadiusFt={radiusInFeet}
        />
      )}
      {offsetTilesUrl && scopedVisibility.offset && (
        <ScopedWellsTileLayers
          idPrefix="offset-wells"
          urlTemplate={offsetTilesUrl}
          lineWidth={OFFSET_LINE_WIDTH}
          searchRadiusFt={radiusInFeet}
        />
      )}

      {/* Wells → Subject — the subject well, drawn BLACK on the inset (INS-8658
          review). The shared WellMapLayer colors the stick-only fallback blue;
          here we render the line directly in black while KEEPING the main map's
          survey-vs-stick concept: survey → solid, stick (no survey) → dotted.
          Inset-only — the main Well Page map (WellMapLayer) is untouched. */}
      {scopedVisibility.subject &&
        subjectProducingWellbores.map((wb) => {
          const geometry = wb.survey ?? wb.stick;
          if (!geometry) return null;
          // Survey = solid black; stick = dotted black (dash pattern preserved).
          const style = wb.survey
            ? LINE_STYLES.BLACK_SOLID
            : {
                ...LINE_STYLES.BLUE_DOTTED,
                'line-color': mapColors.verticalFill,
              };
          return (
            <Line
              key={`inset-subject-${wb.uwi14}`}
              id={`inset-subject-${wb.uwi14}`}
              geojson={geometry}
              style={style}
            />
          );
        })}

      {/* Wells → Subject (estimated) — a synthesized vertical is a near-zero
          length line, invisible on the plan-view inset, so render it as a faded
          surface-hole circle (estimated treatment) rather than a line. */}
      {scopedVisibility.subject && estimatedMarkerData && (
        <Source
          id="inset-subject-estimated"
          type="geojson"
          data={estimatedMarkerData}
        >
          <Layer
            id="inset-subject-estimated-marker"
            type="circle"
            paint={{
              'circle-radius': ESTIMATED_MARKER_RADIUS,
              'circle-color': mapColors.verticalFill,
              'circle-opacity': ESTIMATED_ALPHA,
              'circle-stroke-color': mapColors.verticalFill,
              'circle-stroke-width': 1,
              'circle-stroke-opacity': ESTIMATED_ALPHA,
            }}
          />
        </Source>
      )}

      {showCircle && searchEnvelopeData && (
        <Source id="search-envelope" type="geojson" data={searchEnvelopeData}>
          <Layer
            id="search-envelope-fill"
            type="fill"
            paint={{
              'fill-color': '#FF69B4',
              'fill-opacity': 0.3,
            }}
          />
          <Layer
            id="search-envelope-stroke"
            type="line"
            paint={{
              'line-color': '#FF1493',
              'line-width': 2,
              'line-opacity': 0.8,
            }}
          />
        </Source>
      )}
      {showCircle && boundingBoxData && (
        <Source id="bounding-box" type="geojson" data={boundingBoxData}>
          <Layer
            id="bounding-box-fill"
            type="fill"
            paint={{
              'fill-color': '#000000',
              'fill-opacity': 0.1,
            }}
          />
          <Layer
            id="bounding-box-stroke"
            type="line"
            paint={{
              'line-color': '#000000',
              'line-width': 2,
              'line-opacity': 0.8,
            }}
          />
        </Source>
      )}
      {showSlice && sliceData && (
        <Source id="gun-barrel-slice" type="geojson" data={sliceData}>
          <Layer
            id="gun-barrel-slice-fill"
            type="fill"
            paint={{
              'fill-color': '#FF69B4',
              'fill-opacity': 0.5,
            }}
          />
          <Layer
            id="gun-barrel-slice-stroke"
            type="line"
            paint={{
              'line-color': '#FF1493',
              'line-width': 2,
              'line-opacity': 0.9,
            }}
          />
        </Source>
      )}
      {directionArrowData && !activeSliceCoords && (
        <Source id="direction-arrow" type="geojson" data={directionArrowData}>
          <Layer
            id="direction-arrow-fill"
            type="fill"
            paint={{
              'fill-color': '#FF69B4',
              'fill-opacity': 0.85,
            }}
          />
          <Layer
            id="direction-arrow-stroke"
            type="line"
            paint={{
              'line-color': '#FF1493',
              'line-width': 1.5,
              'line-opacity': 1,
            }}
          />
        </Source>
      )}
      {gunBarrelDirectionData && (
        <Source
          id="gun-barrel-direction"
          type="geojson"
          data={gunBarrelDirectionData}
        >
          <Layer
            id="gun-barrel-direction-fill"
            type="fill"
            paint={{
              'fill-color': '#FF69B4',
              'fill-opacity': 0.85,
            }}
          />
          <Layer
            id="gun-barrel-direction-stroke"
            type="line"
            paint={{
              'line-color': '#FF1493',
              'line-width': 1.5,
              'line-opacity': 1,
            }}
          />
        </Source>
      )}
      {highlightedSurvey && (
        <Source
          id="highlighted-wellbore"
          type="geojson"
          data={highlightedSurvey}
        >
          <Layer
            id="highlighted-wellbore-halo"
            type="line"
            paint={{
              'line-color': highlightedSurveyColor,
              ...HIGHLIGHT_HALO_PAINT,
            }}
          />
          <Layer
            id="highlighted-wellbore-line"
            type="line"
            paint={{
              'line-color': highlightedSurveyColor,
              ...HIGHLIGHT_LINE_PAINT,
            }}
          />
        </Source>
      )}
      {/* Offset well: filter the shared wells-v2 tile source by UWI so we
          paint over the well's existing line without an extra fetch. */}
      {highlightedUwi && (
        <>
          <Layer
            id="highlighted-offset-wellbore-halo"
            source="wells-v2"
            source-layer="survey"
            type="line"
            filter={['==', ['get', 'uwi'], highlightedUwi]}
            paint={{
              'line-color':
                STATUS_HIGHLIGHT_MATCH_EXPRESSION as unknown as string,
              ...HIGHLIGHT_HALO_PAINT,
            }}
          />
          <Layer
            id="highlighted-offset-wellbore-line"
            source="wells-v2"
            source-layer="survey"
            type="line"
            filter={['==', ['get', 'uwi'], highlightedUwi]}
            paint={{
              'line-color':
                STATUS_HIGHLIGHT_MATCH_EXPRESSION as unknown as string,
              ...HIGHLIGHT_LINE_PAINT,
            }}
          />
          {/* Permits have no survey — they render as dashed sticks, so the
              highlight must target the wells-v2 `stick` source-layer too.
              `has_survey` filter keeps it to no-survey wells (permits). */}
          <Layer
            id="highlighted-offset-stick-halo"
            source="wells-v2"
            source-layer="stick"
            type="line"
            filter={[
              'all',
              ['==', ['get', 'uwi'], highlightedUwi],
              ['!', ['get', 'has_survey']],
            ]}
            paint={{
              'line-color':
                STATUS_HIGHLIGHT_MATCH_EXPRESSION as unknown as string,
              ...HIGHLIGHT_HALO_PAINT,
            }}
          />
          <Layer
            id="highlighted-offset-stick-line"
            source="wells-v2"
            source-layer="stick"
            type="line"
            filter={[
              'all',
              ['==', ['get', 'uwi'], highlightedUwi],
              ['!', ['get', 'has_survey']],
            ]}
            paint={{
              'line-color':
                STATUS_HIGHLIGHT_MATCH_EXPRESSION as unknown as string,
              ...HIGHLIGHT_LINE_PAINT,
              'line-dasharray': [6, 2],
            }}
          />
        </>
      )}
    </Map>
  );
};

export default WellSelectorMap;
